import { AfterViewInit, Component, DestroyRef, ElementRef, inject, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormGroup, FormControl, UntypedFormControl } from '@angular/forms';
import _ from 'lodash';
import { BulkCheckoutService } from '../../services/bulk-checkout.service';
import { LoadingService } from 'app/core/services/loading.service';
import { MdbModalRef } from 'mdb-angular-ui-kit/modal';
import { MdbpInputComponent } from 'app/core/components/mdb-input/mdb-input.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-bulk-checkout',
  templateUrl: './bulk-checkout.component.html',
  styleUrls: ['./bulk-checkout.component.scss']
})
export class BulkCheckoutComponent implements OnInit, AfterViewInit, OnDestroy {
  destroyRef = inject(DestroyRef)
  @ViewChild('lastItem', { static: false }) lastItemRef!: ElementRef;
  @ViewChild('editMemoInput', { static: false }) memoInput: MdbpInputComponent;
  
  public search: UntypedFormControl = new UntypedFormControl('', []);

  public modalSubHeader = 'Choose donors to include in this bulk checkout'
  public p: number = 1;
  public userWithSavedCart: any[] = [];
  public isAllSelected = false;
  public filteredDonors: any[] = [];

  public activeScreen = 'list';
  public status = 'checkout';
  public isShowTryAgain = false;
  public finalIcon = '<i class="fas fa-check-circle green-text headline-2--weight-900"></i>';
  public isStopHovered = false;

  public finished: string[] = [];
  public resolved: string[] = [];
  public rejected: string[] = [];

  public resolvedArr: any[] = [];
  public rejectedArr: any[] = [];
  
  @Input() data: any;
  
  public editMemoForm = new FormGroup({
    memo: new FormControl('', [])
  });

  constructor(
    private element: ElementRef,
    public bulkCheckoutService: BulkCheckoutService,
    public loadingService: LoadingService,
    public modalRef: MdbModalRef<BulkCheckoutComponent>
  ) { }

  ngOnInit(): void {
    // If called from inPersonEvent - directly show screen with list of attendees
    // If called from auction - firstly show screen with select checkout type
    if (this.data.placeOfCall === 'inPersonEvent') {
      window['eventReportInvitationsModal'].getInPersonEventAttendees(window['eventReportInvitationsModal'].event.id)
      .then(attendees => {
        this.initializeCheckoutList(_.cloneDeep(attendees));
      });
    } else if (this.data.placeOfCall === 'auction') {
      this.activeScreen = 'selectCheckoutType';      
      this.modalSubHeader = 'Check out all pending items or only auction items in carts';
    }
  }

  private initializeCheckoutList(list: any[]) {
    this.userWithSavedCart = list.filter(user => (
      user.preSwipreFundingSource && 
      user.donationCart &&  
      user.donationCart.items && 
      this.getPaymentNum(user.donationCart) > 0 
    )).map(user => ({
      ...user,
      checked: false,
      checkoutStatus: '',
      checkoutStatusIcon: null,
      errorMessage: null,
      last: null
    }));

    this.filteredDonors = this.userWithSavedCart;
    this.isAllSelected = false;
    this.bulkCheckoutService.placeOfCall = this.data.placeOfCall;

    this.search.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(search => {
      if (!search) {
        this.filteredDonors = this.userWithSavedCart;
        return;
      }
      
      this.filteredDonors = _.filter(this.userWithSavedCart, (user) => {
        return user.donorInfo.name.toLowerCase().includes(search.toLowerCase()) ||
          user.donorInfo.email.toLowerCase().includes(search.toLowerCase());
      });
      this.p = 1;
    });
  }

  ngAfterViewInit(): void {
    const styles = window['documentManager'].createElement('link');
    styles.href = 'paybee-spa/styles.css?v=398';
    styles.rel = 'stylesheet';
    window['$'](this.element.nativeElement).append(styles);
  }

  onChange(event, id): void {
    const donor = this.userWithSavedCart.find(opt => opt.id === id);
    donor.checked = !donor.checked;

    this.bulkCheckoutService.selectedDonors = this.userWithSavedCart
      .filter(opt => opt.checked === true)
      .map(donor => {
        donor.checkoutStatus = '';
        donor.checkoutStatusIcon = null;
        donor.errorMessage = null;
        donor.last = null;
        return donor;
      });
    this.bulkCheckoutService.selectedCount = this.bulkCheckoutService.selectedDonors?.length || 0;
  }

  onGetFeesChange(event, id){
    const donor = this.bulkCheckoutService.selectedDonors.find(opt => opt.id === id)
    if (this.data.placeOfCall === 'inPersonEvent') {
      donor.preSwipreFundingSource.inPersonEventFeeCovered[0].FeesPaidByDonor = !donor.preSwipreFundingSource.inPersonEventFeeCovered[0].FeesPaidByDonor;
    } else if (this.data.placeOfCall === 'auction') {
      donor.preSwipreFundingSource.auctionCampaignFeeCovered[0].FeesPaidByDonor = !donor.preSwipreFundingSource.auctionCampaignFeeCovered[0].FeesPaidByDonor;
    }
  }

  getFeesPaidByDonor(option){
    let feesPaidByDonor = false;

    if (this.data.placeOfCall === 'inPersonEvent') {
      const inPersonEventFeeCovered = option.preSwipreFundingSource.inPersonEventFeeCovered
      feesPaidByDonor = inPersonEventFeeCovered[0].FeesPaidByDonor
    } else if (this.data.placeOfCall === 'auction') {
      const auctionCampaignFeeCovered = option.preSwipreFundingSource.auctionCampaignFeeCovered
      feesPaidByDonor = auctionCampaignFeeCovered[0].FeesPaidByDonor
    }

    return feesPaidByDonor
  }
  
  selectAll(): void {
    this.userWithSavedCart.map(option => {
      option.checked = true;
      return option;
    });

    this.isAllSelected = true;
    this.bulkCheckoutService.selectedCount = this.userWithSavedCart.length;
    this.bulkCheckoutService.selectedDonors = this.userWithSavedCart.map(donor=>{
      donor.checkoutStatus = ''
      donor.checkoutStatusIcon = null
      donor.errorMessage = null
      donor.last = null
      return donor
    });
  }

  getCartInfo(cartInfo){
    let img = ''
    let cart = cartInfo.sourceName.split(' ')
    let lastNumbers = cart[cart.length - 1]

    switch (cartInfo.cardBrand){
      case 'Visa': 
      case 'visa':
        img = "<img class='mr-1 payment-card p-1' src='images/payment-cards/Visa_2021.png'/>";
        break
      case 'MasterCard':
        img = "<img class='mr-1 payment-card payment-card--mastercard' src='images/payment-cards/master-cart.png'/>";
        break
      case 'Discover':
        img = "<img class='mr-1 payment-card' src='images/payment-cards/discover.png'/>";
        break
      case 'Diners ': 
      case 'Diners Club':
        img = "<img class='mr-1 payment-card payment-card--Diners' src='images/payment-cards/dinersClub.png'/>";
        break
      case 'American Express':
        img = "<img class='mr-1 payment-card' src='images/payment-cards/american-express.png'/>";
        break
      case 'JCB':
        img = "<img class='mr-1 payment-card' src='images/payment-cards/JCB.png'/>";
        break
      default: 
        img =  "<img class='mr-1 payment-card px-2' src='images/payment-cards/bank-icon.png'/>";
    }

    return `<div class='d-flex align-items-center'>${img}<span class='body-2' style='color:#676767' span>*${lastNumbers}</span></div>`

  }

  onSelectCheckoutType(onlyAuctionItems: boolean) {
    this.bulkCheckoutService.getAuctionCartList(this.data.campaignId, onlyAuctionItems).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((list: any[]) => {
      // Map the cartList ot have similar structure as inPersonEvent attendees
      const cartList = list.map((cart, index) => ({
        id: cart.id,
        donationCart: cart,
        donorInfo: cart.donorInfo,
        preSwipreFundingSource: cart.auctionSavedFundingSource
      }));
      this.initializeCheckoutList(cartList);
    })

    this.activeScreen = 'list';
    this.status = 'checkout';
    this.modalSubHeader = 'Choose donors to include in this bulk checkout';
  }

  onSelectClick(){
    this.activeScreen = 'selectedList'
    this.modalSubHeader = `${this.bulkCheckoutService.selectedDonors.length} Donor(s) selected for checkout`
  }

  onBackClick(){
    this.activeScreen = 'list'
    this.status = 'checkout'
    this.modalSubHeader = 'Choose donors to include in this bulk checkout'
  }

  unSelectAll(): void {
    this.userWithSavedCart.map(option => {
      option.checked = false;
      return option;
    });

    this.isAllSelected = false;
    this.bulkCheckoutService.selectedCount = 0;
    this.bulkCheckoutService.selectedDonors = [];
  }

  resetSearch() {
    this.search.setValue('');
  }

  getPaymentStr({items}){
      let total = 0
      items.forEach(item=>{
          total += item.donationInfo.amount
      })
      return  `$${this.bulkCheckoutService.addCommasToNumber(total)}`
  }

  getPaymentNum({items}){
    let total = 0
    items.forEach(item=>{
        total += item.donationInfo.amount
    })
    return total
  }

  getTotalDonated(){
    let total = 0
    this.resolvedArr.forEach(donor=>{
      total +=  this.getPaymentNum(donor.donationCart)
    })
    return  `$${this.bulkCheckoutService.addCommasToNumber(total)}`
  }

  onCheckoutClick(users){
    this.loadingService.enabled = false
    this.bulkCheckoutService.isBulkCheckout = true

    this.status = 'in-progress'

    this.bulkCheckoutService.bulkCheckout(users).then(list=>{
      this.loadingService.enabled = true
      window['$']('#bulkCheckoutHeader').hide()
      this.resolved = []
      this.rejected = []

      if(this.bulkCheckoutService.checkoutStatus !== 'stopped'){
        this.activeScreen = 'result'
      }
      this.finished = [...this.finished,...list]

      this.finished.forEach((item)=>{
        let itemStatus = item.split('_')[0]
        let itemId = item.split('_')[1] 
        if(itemStatus == 'resolved'){
          this.resolved.push(itemId)
        }else{
          this.rejected.push(itemId)
        }
      })

      if(this.rejected.length){
        this.isShowTryAgain = true
      }

      this.resolvedArr = this.bulkCheckoutService.selectedDonors.filter(donor =>this.resolved.includes(donor.donationCart.id))
      this.rejectedArr = this.bulkCheckoutService.selectedDonors.filter(donor =>this.rejected.includes(donor.donationCart.id))

      if(this.rejectedArr.length !== 0){
        this.finalIcon = '<i class="fas fa-check-circle yellow-text headline-2--weight-900"></i>'
      } else if (this.resolvedArr.length == 0){
        this.finalIcon = '<i class="fas fa-check-circle red-text headline-2--weight-900"></i>'
      }
      
    })
  }

  onStartCheckout(){
    this.onCheckoutClick(this.bulkCheckoutService.selectedDonors)
  }

  onStopCheckout(){
    this.bulkCheckoutService.onStopCheckout()
    this.status = 'stopped'
  }

  onTryAgain(donor){
      donor.checkoutStatus = '<span class="subtitle-2 font-italic grey-texty">Processing…<span/>'
      donor.checkoutStatusIcon = '<i class="fas fa-spinner checkout-spiner"></i>'
      donor.errorMessage = ''
      this.loadingService.enabled = false 
      this.bulkCheckoutService.bulkCheckout([donor]).then((item)=>{
        this.loadingService.enabled = true
        let itemStatus = item[0].split('_')[0]
        let itemId = item[0].split('_')[1] 
        if(itemStatus == 'resolved'){
          this.resolved.push(itemId)
          this.resolvedArr.push(donor)
          this.rejectedArr = this.rejectedArr.filter((donor)=>donor.donationCart.id !== itemId)
          donor.checkoutStatus = '<span class=" subtitle-2 green-text">Completed</span>'
          donor.checkoutStatusIcon = '<i class="fas fa-check-circle green-text"></i>'

        }else{
          donor.checkoutStatus = '<span class="subtitle-2 red-text ">Failed</span>'
          donor.checkoutStatusIcon = '<i class="fas fa-exclamation-triangle red-text"></i>'
        }
        if(this.rejectedArr.length == 0){
          this.finalIcon = '<i class="fas fa-check-circle green-text headline-2--weight-900"></i>'
        } 
    })
  }

  scrollToLastItem(elem) {
    if (elem) {
      elem.nativeElement.scrollIntoView({ behavior: 'smooth' });
    }
  }

  close(refreshAttendees = false){
    this.modalRef.close(refreshAttendees);
    this.bulkCheckoutService.isBulkCheckout = false;
  }

  onContinue(){
    let finishedCleanId = this.finished.map((user)=>(user.split('_')[1]))

    this.bulkCheckoutService.checkoutStatus = 'working'
    this.status = 'in-progress'

    let notFinished = this.bulkCheckoutService.selectedDonors.filter(donor => !finishedCleanId.includes(donor.donationCart.id))
    this.onCheckoutClick(notFinished)
  }

  currentOption = null;
  onEditMemo(option){
    this.modalSubHeader = `Edit Memo For ${option.donorInfo.name}`
    this.currentOption = option;
    this.editMemoForm.patchValue({
      memo: option.donationCart.memo || ''
    });
    this.activeScreen = 'edit-memo'
    setTimeout(() => {
      if (this.memoInput?.inputName) {
        this.memoInput.focused = true;
        this.memoInput.inputName.nativeElement.focus();
      }
    }, 0);
  }

  closeMemoScreen(){
    this.modalSubHeader = 'Choose donors to include in this bulk checkout';
    this.activeScreen = 'list';
  }

  saveEditedMemo(){
    const newMemo = this.editMemoForm.get('memo').value || '';
    const index = this.userWithSavedCart.findIndex(el=> el.id === this.currentOption.id)
    this.userWithSavedCart[index].donationCart.memo = newMemo
    this.activeScreen = 'list'
    this.modalSubHeader = 'Choose donors to include in this bulk checkout'
    const donationCartId = this.userWithSavedCart[index].donationCart.id;
    this.bulkCheckoutService.saveMemo(donationCartId, newMemo).pipe(
      takeUntilDestroyed(this.destroyRef),
    )
    .subscribe();
  }

  ngOnDestroy(): void {
    this.bulkCheckoutService.selectedCount = 0;
    this.bulkCheckoutService.finished = 0;
    this.bulkCheckoutService.selectedDonors = [];
    this.bulkCheckoutService.checkoutStatus = 'working';
    this.bulkCheckoutService.sendAknowledgment = true;
    this.bulkCheckoutService.isBulkCheckout = false;

    this.userWithSavedCart = [];
    this.filteredDonors = [];
    this.finished = [];
    this.resolved = [];
    this.rejected = [];
    this.resolvedArr = [];
    this.rejectedArr = [];
  }
}
